/**
 * Error Logging Service
 *
 * Production-grade error tracking that integrates with external services like Sentry, LogRocket, or custom backends.
 * Falls back gracefully when external services are unavailable.
 */
import logger from "@shared/utils/logger";
import { getBuildTimeAppVersionInfo } from "@shared/config/appInfo";
import { bootstrapCsrfToken } from "@shared/services/apiClient";

export interface ErrorContext {
  feature?: string;
  userId?: string;
  userEmail?: string;
  url?: string;
  componentStack?: string;
  additionalData?: Record<string, any>;
}

export interface ErrorLogEntry {
  error: Error;
  context: ErrorContext;
  timestamp: Date;
  environment: string;
  userAgent: string;
  browserInfo: {
    name?: string;
    version?: string;
    os?: string;
  };
}

interface FrontendAuditPayload {
  message: string;
  stack?: string;
  feature?: string;
  url: string;
  timestamp: string;
  environment: string;
  userAgent: string;
  browserInfo: {
    name?: string;
    version?: string;
    os?: string;
  };
  additionalData?: Record<string, any>;
}

export interface DiagnosticSubmissionResult {
  referenceId: string;
  recentErrorCount: number;
  destination: string;
}

class ErrorLoggingService {
  private readonly SENTRY_DSN: string | undefined;
  private readonly SENTRY_DEV_ENABLED: boolean;
  private readonly SENTRY_REPLAY_ENABLED: boolean;
  private readonly API_ENDPOINT: string;
  private readonly MAX_QUEUE_SIZE = 50;
  private errorQueue: ErrorLogEntry[] = [];
  private isInitialized = false;

  constructor() {
    this.SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
    const sentryDevFlag = String(import.meta.env.VITE_SENTRY_DEV || "")
      .trim()
      .toLowerCase();
    const sentryReplayFlag = String(import.meta.env.VITE_SENTRY_REPLAY || "")
      .trim()
      .toLowerCase();
    this.SENTRY_DEV_ENABLED = sentryDevFlag === "1" || sentryDevFlag === "true";
    this.SENTRY_REPLAY_ENABLED =
      sentryReplayFlag === "1" || sentryReplayFlag === "true";
    // VITE_API_URL in dev-local is a bare host (e.g. http://localhost:4000),
    // not a base-API path.  Mirror the same logic used by apiClient.ts so the
    // path always resolves to /<host>/api/…  or just /api/… via the Vite proxy.
    const envUrl = (import.meta.env.VITE_API_URL as string | undefined) || "";
    const isLocalhostUrl = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(
      envUrl.replace(/\/api$/, ""),
    );
    if (envUrl && !isLocalhostUrl) {
      // Non-localhost explicit URL — append /api if not already present
      this.API_ENDPOINT = envUrl.endsWith("/api")
        ? envUrl
        : `${envUrl.replace(/\/$/, "")}/api`;
    } else {
      // Localhost or no VITE_API_URL — use relative path (Vite proxy)
      this.API_ENDPOINT = "/api";
    }
  }

  /**
   * Initialize error logging service
   * This should be called early in application startup
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Initialize Sentry if DSN is configured
      if (this.SENTRY_DSN) {
        // Dynamically import Sentry to avoid bundling it if not used
        const Sentry = await this.loadSentry();
        if (Sentry) {
          const sentryDevEnabled = this.SENTRY_DEV_ENABLED;
          const enableReplay =
            import.meta.env.PROD && this.SENTRY_REPLAY_ENABLED;
          const integrations = [Sentry.browserTracingIntegration()];

          if (enableReplay && typeof Sentry.replayIntegration === "function") {
            integrations.push(
              Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true,
              }),
            );
          }

          Sentry.init({
            dsn: this.SENTRY_DSN,
            environment: import.meta.env.MODE || "development",
            integrations,
            // Performance Monitoring
            tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
            // Session Replay stays opt-in to avoid its default runtime overhead.
            replaysSessionSampleRate: enableReplay ? 0.05 : 0,
            replaysOnErrorSampleRate: enableReplay ? 0.25 : 0,
            // Filtering
            beforeSend(event: any, hint: any) {
              // Don't send errors in development unless explicitly enabled
              if (import.meta.env.DEV && !sentryDevEnabled) {
                return null;
              }
              return event;
            },
          });
          logger.info("[ErrorLogging] Sentry initialized");
        }
      }

      this.isInitialized = true;
    } catch (error) {
      logger.error("[ErrorLogging] Failed to initialize:", error);
      // Continue without external service
    }
  }

  /**
   * Log an error with context
   */
  logError(error: Error, context: ErrorContext = {}): void {
    const entry: ErrorLogEntry = {
      error,
      context,
      timestamp: new Date(),
      environment: import.meta.env.MODE || "development",
      userAgent: navigator.userAgent,
      browserInfo: this.getBrowserInfo(),
    };

    // Always log to console in development
    if (import.meta.env.DEV) {
      logger.info(`[Error] ${context.feature || "Unknown Feature"}`);
      logger.error("Unhandled error", error);
      logger.info("Context:", context);
      logger.info("User Agent:", navigator.userAgent);
      // logger.groupEnd();
    }

    // Add to queue
    this.addToQueue(entry);

    // Send to external services
    this.sendToExternalServices(entry);

    // Send to backend API for audit trail
    this.sendToBackend(entry);
  }

  /**
   * Set user context for error reporting
   */
  setUser(user: { id: string; email?: string; username?: string }): void {
    this.loadSentry().then((Sentry) => {
      if (Sentry) {
        Sentry.setUser({
          id: user.id,
          email: user.email,
          username: user.username,
        });
      }
    });
  }

  /**
   * Clear user context (on logout)
   */
  clearUser(): void {
    this.loadSentry().then((Sentry) => {
      if (Sentry) {
        Sentry.setUser(null);
      }
    });
  }

  /**
   * Add breadcrumb for debugging context
   */
  addBreadcrumb(
    message: string,
    category: string,
    data?: Record<string, any>,
  ): void {
    this.loadSentry().then((Sentry) => {
      if (Sentry) {
        Sentry.addBreadcrumb({
          message,
          category,
          data,
          level: "info",
          timestamp: Date.now() / 1000,
        });
      }
    });
  }

  addPlaybackTelemetry(data: {
    recordingId?: string;
    rendererMode?: "gpu" | "cpu" | "unknown";
    fallbackReason?: string;
    [key: string]: any;
  }): void {
    const rendererMode =
      data.rendererMode === "gpu" || data.rendererMode === "cpu"
        ? data.rendererMode
        : "unknown";
    const allowedFallbackReasons = new Set([
      "none",
      "webgl_unavailable",
      "offscreen_unavailable",
      "gpu_disabled_by_user",
      "gpu_context_lost",
      "gpu_runtime_error",
      "cpu_degraded_highspeed",
      "cpu_video_jump_mode",
      "unknown",
    ]);
    const fallbackReason =
      typeof data.fallbackReason === "string" &&
      allowedFallbackReasons.has(data.fallbackReason)
        ? data.fallbackReason
        : "unknown";

    this.addBreadcrumb("eeg_playback_perf", "eeg-playback", {
      ...data,
      renderer_mode: rendererMode,
      fallback_reason: fallbackReason,
    });
  }

  /**
   * Get recent error queue for diagnostics
   */
  getRecentErrors(): ErrorLogEntry[] {
    return [...this.errorQueue];
  }

  /**
   * Clear error queue
   */
  clearQueue(): void {
    this.errorQueue = [];
  }

  /**
   * Send a user-requested diagnostic bundle to the backend audit endpoint.
   */
  async submitDiagnosticReport(options?: {
    source?: string;
    note?: string;
    currentUrl?: string;
  }): Promise<DiagnosticSubmissionResult> {
    const buildInfo = getBuildTimeAppVersionInfo();
    const referenceId = `diag-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const recentErrors = this.errorQueue.slice(-10).map((entry) => ({
      message: entry.error.message,
      stack: entry.error.stack,
      feature: entry.context.feature,
      url: entry.context.url,
      timestamp: entry.timestamp.toISOString(),
      environment: entry.environment,
      browserInfo: entry.browserInfo,
      additionalData: entry.context.additionalData,
    }));

    const payload: FrontendAuditPayload = {
      message: "User submitted frontend diagnostics",
      feature: "support-diagnostics",
      url: options?.currentUrl || window.location.href,
      timestamp: new Date().toISOString(),
      environment: import.meta.env.MODE || "development",
      userAgent: navigator.userAgent,
      browserInfo: this.getBrowserInfo(),
      additionalData: {
        source: options?.source || "support-feedback",
        note: options?.note || undefined,
        referenceId,
        buildInfo,
        currentPath: window.location.pathname,
        recentErrorCount: recentErrors.length,
        recentErrors,
      },
    };

    await this.postAuditPayload(payload, false);

    return {
      referenceId,
      recentErrorCount: recentErrors.length,
      destination: `${this.API_ENDPOINT}/audit/frontend-error`,
    };
  }

  // ========== Private Methods ==========

  private async loadSentry(): Promise<any> {
    if (!this.SENTRY_DSN) return null;

    try {
      // Dynamically import Sentry only when needed and configured
      const module = await import("@sentry/react");
      return module;
    } catch (error) {
      logger.warn(
        "[ErrorLogging] Sentry package not available. Install @sentry/react for error tracking.",
      );
      return null;
    }
  }

  private addToQueue(entry: ErrorLogEntry): void {
    this.errorQueue.push(entry);

    // Keep queue size manageable
    if (this.errorQueue.length > this.MAX_QUEUE_SIZE) {
      this.errorQueue.shift();
    }
  }

  private async sendToExternalServices(entry: ErrorLogEntry): Promise<void> {
    try {
      const Sentry = await this.loadSentry();
      if (Sentry) {
        Sentry.captureException(entry.error, {
          contexts: {
            feature: {
              name: entry.context.feature || "unknown",
            },
          },
          user: entry.context.userId
            ? {
                id: entry.context.userId,
                email: entry.context.userEmail,
              }
            : undefined,
          tags: {
            feature: entry.context.feature,
            environment: entry.environment,
          },
          extra: entry.context.additionalData,
        });
      }
    } catch (error) {
      logger.warn("[ErrorLogging] Failed to send to external service:", error);
    }
  }

  private async sendToBackend(entry: ErrorLogEntry): Promise<void> {
    const payload = this.createAuditPayload(entry);

    await this.postAuditPayload(payload, true);
  }

  private createAuditPayload(entry: ErrorLogEntry): FrontendAuditPayload {
    return {
      message: entry.error.message,
      stack: entry.error.stack,
      feature: entry.context.feature,
      url: entry.context.url || window.location.href,
      timestamp: entry.timestamp.toISOString(),
      environment: entry.environment,
      userAgent: entry.userAgent,
      browserInfo: entry.browserInfo,
      additionalData: entry.context.additionalData,
    };
  }

  private async postAuditPayload(
    payload: FrontendAuditPayload,
    swallowErrors: boolean,
  ): Promise<void> {
    try {
      const csrfToken = await bootstrapCsrfToken();

      const response = await fetch(`${this.API_ENDPOINT}/audit/frontend-error`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!response.ok && !swallowErrors) {
        throw new Error(
          `Diagnostics upload failed with status ${response.status}`,
        );
      }
    } catch (error) {
      if (!swallowErrors) {
        throw error;
      }
    }
  }

  private getBrowserInfo(): { name?: string; version?: string; os?: string } {
    const ua = navigator.userAgent;
    let browserName, browserVersion, os;

    // Detect browser
    if (ua.includes("Firefox/")) {
      browserName = "Firefox";
      browserVersion = ua.match(/Firefox\/([\d.]+)/)?.[1];
    } else if (ua.includes("Edg/")) {
      browserName = "Edge";
      browserVersion = ua.match(/Edg\/([\d.]+)/)?.[1];
    } else if (ua.includes("Chrome/")) {
      browserName = "Chrome";
      browserVersion = ua.match(/Chrome\/([\d.]+)/)?.[1];
    } else if (ua.includes("Safari/")) {
      browserName = "Safari";
      browserVersion = ua.match(/Version\/([\d.]+)/)?.[1];
    }

    // Detect OS
    if (ua.includes("Windows")) os = "Windows";
    else if (ua.includes("Mac OS")) os = "macOS";
    else if (ua.includes("Linux")) os = "Linux";
    else if (ua.includes("Android")) os = "Android";
    else if (ua.includes("iOS")) os = "iOS";

    return { name: browserName, version: browserVersion, os };
  }
}

// Export singleton instance
export const errorLoggingService = new ErrorLoggingService();

// Auto-initialize on import
errorLoggingService.initialize();
